import React, { useEffect, useRef, useState } from 'react';
import { Star } from 'lucide-react';
import { realReviews } from '../data/reviews';

const Stars = ({ n = 5 }) => (
  <div className="flex gap-0.5" aria-label={`${n} out of 5 stars`}>
    {Array.from({ length: n }).map((_, i) => (
      <Star key={i} size={14} className="fill-[#F39200] text-[#F39200]" />
    ))}
  </div>
);

const Card = ({ r }) => (
  <div className="shrink-0 w-[320px] md:w-[400px] bg-white rounded-2xl border border-black/5 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.08)] p-6 mr-5 flex flex-col">
    <div className="flex items-center justify-between">
      <Stars n={r.rating} />
      <span className="text-[10px] font-semibold uppercase tracking-wider text-[#F39200] bg-[#F39200]/10 px-2 py-1 rounded-full">
        {r.category}
      </span>
    </div>
    <p className="mt-4 text-[14px] leading-relaxed text-neutral-700 flex-1">&ldquo;{r.quote}&rdquo;</p>
    <div className="mt-5 flex items-center gap-3 pt-4 border-t border-black/5">
      <div className="w-10 h-10 rounded-full bg-[#F39200]/10 text-[#F39200] flex items-center justify-center text-sm font-bold shrink-0">
        {r.initials}
      </div>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-neutral-900 truncate">{r.name}</div>
        <div className="text-[11px] text-neutral-500 truncate">{r.company}</div>
      </div>
    </div>
  </div>
);

const Marquee = ({ items, reverse = false, speed = 28 }) => {
  const ref = useRef(null);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let raf; let last = performance.now();
    const tick = (now) => {
      const dt = (now - last) / 1000; last = now;
      if (!paused) {
        el.scrollLeft += (reverse ? -1 : 1) * speed * dt;
        const max = el.scrollWidth / 2;
        if (!reverse && el.scrollLeft >= max) el.scrollLeft -= max;
        if (reverse && el.scrollLeft <= 0) el.scrollLeft += max;
      }
      raf = requestAnimationFrame(tick);
    };
    if (reverse) el.scrollLeft = el.scrollWidth / 2;
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, reverse, speed]);
  const loop = [...items, ...items];
  return (
    <div
      ref={ref}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="flex overflow-x-hidden no-scrollbar py-3"
    >
      {loop.map((r, i) => <Card key={i} r={r} />)}
    </div>
  );
};

const Reviews = () => (
  <section className="bg-[#FAF2E8] py-16 md:py-20" data-testid="reviews-section">
    <div className="max-w-7xl mx-auto px-5 lg:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">Verified reviews</div>
          <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900 leading-[1.1]">
            What our clients{' '}
            <span className="relative inline-block text-[#F39200]">say about us
              <svg className="absolute -bottom-2 left-0 w-full" height="10" viewBox="0 0 280 10" fill="none">
                <path d="M2 7 C 70 1, 160 1, 278 6" stroke="#F39200" strokeWidth="2" strokeLinecap="round" fill="none" />
              </svg>
            </span>
          </h2>
        </div>
        <div className="flex items-center gap-4 bg-white rounded-2xl border border-black/5 shadow-sm px-5 py-3">
          <div className="text-3xl font-extrabold text-neutral-900">5.0</div>
          <div>
            <Stars n={5} />
            <div className="text-xs text-neutral-500 mt-0.5">Rated by verified clients</div>
          </div>
        </div>
      </div>
    </div>
    <div className="mt-10 space-y-2">
      <Marquee items={realReviews} speed={26} />
      <Marquee items={[...realReviews].reverse()} reverse speed={24} />
    </div>
  </section>
);

export default Reviews;
